(function () {
  var SELECTOR_FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var ERROR_CODES = {
    CART_CHANGED: 'cartChanged',
    ITEM_OUT_OF_STOCK: 'insufficientInventory',
    INSUFFICIENT_QUANTITY: 'insufficientInventory',
    LOCATION_UNAVAILABLE: 'locationUnavailable',
    PRODUCT_NOT_ELIGIBLE: 'productNotEligible',
    TRANSFER_NOT_AVAILABLE: 'transferUnavailable',
    CUSTOMER_DATA_INVALID: 'customerInvalid',
    VALIDATION_TOKEN_EXPIRED: 'validationExpired',
    RESERVATION_ALREADY_EXISTS: 'reservationExists',
    ORDER_CREATION_FAILED: 'orderFailed',
    INVENTORY_RESERVATION_FAILED: 'inventoryFailed',
    RATE_LIMITED: 'rateLimited',
    SHOPIFY_API_ERROR: 'shopifyApiError',
    UNKNOWN_ERROR: 'generalError'
  };

  function readJson(script, fallback) {
    if (!script) return fallback;
    try {
      return JSON.parse(script.textContent || '');
    } catch (error) {
      return fallback;
    }
  }

  function createElement(tagName, className, text) {
    var element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined && text !== null) element.textContent = String(text);
    return element;
  }

  function appendText(parent, tagName, className, text) {
    if (text === undefined || text === null || text === '') return null;
    var child = createElement(tagName, className, text);
    parent.appendChild(child);
    return child;
  }

  function normalizePhone(value) {
    var phone = String(value || '')
      .trim()
      .replace(/[\s().-]+/g, '')
      .replace(/^00/, '+');
    // Local MX numbers (10 digits) are sent in E.164 so the POS receives a consistent format.
    if (/^[0-9]{10}$/.test(phone)) return '+52' + phone;
    return phone;
  }

  function safeText(value, fallback) {
    if (value === undefined || value === null || value === '') return fallback || '';
    return String(value);
  }

  function stableJson(value) {
    if (Array.isArray(value)) return value.map(stableJson);
    if (value && typeof value === 'object') {
      return Object.keys(value).sort().reduce(function (acc, key) {
        acc[key] = stableJson(value[key]);
        return acc;
      }, {});
    }
    return value;
  }

  var STORAGE_KEY = 'fs:pickup-customer';
  var STORED_FIELDS = ['firstName', 'lastName', 'phone', 'email', 'contactPreference'];

  // localStorage can throw (private mode, blocked storage), so every access is guarded.
  function readStoredCustomer() {
    try {
      var data = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
      return data && typeof data === 'object' ? data : null;
    } catch (error) {
      return null;
    }
  }

  function writeStoredCustomer(data) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      // Autofill is a convenience; ignore storage failures.
    }
  }

  function pickStoredFields(customer) {
    return STORED_FIELDS.reduce(function (acc, key) {
      acc[key] = customer[key] || '';
      return acc;
    }, {});
  }

  function generateIdempotencyKey() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'pickup-' + Date.now() + '-' + Math.random().toString(16).slice(2);
  }

  class InStorePickup extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.requestController = null;
      this.previousActiveElement = null;
      this.locationsLoaded = false;
      this.pendingReservationPayload = null;
      this.idempotencyKey = null;
      this.state = {
        step: 'customer',
        loading: false,
        cart: null,
        validatedCartSignature: null,
        customer: null,
        locations: [],
        selectedLocationId: null,
        validation: null,
        validationToken: null,
        validationExpiresAt: null,
        options: [],
        selectedOptionId: null,
        reservation: null,
        error: null
      };
    }

    connectedCallback() {
      this.config = readJson(this.querySelector('[data-in-store-pickup-config]'), {});
      this.customer = readJson(this.querySelector('[data-pickup-customer]'), {});
      this.refs = {
        openButton: this.querySelector('[data-pickup-open]'),
        openLabel: this.querySelector('[data-pickup-open-label]'),
        triggerError: this.querySelector('[data-pickup-trigger-error]'),
        overlay: this.querySelector('[data-pickup-overlay]'),
        modal: this.querySelector('[data-pickup-modal]'),
        closeButton: this.querySelector('[data-pickup-close]'),
        cancelButton: this.querySelector('[data-pickup-cancel]'),
        backButton: this.querySelector('[data-pickup-back]'),
        primaryButton: this.querySelector('[data-pickup-primary]'),
        form: this.querySelector('[data-pickup-customer-form]'),
        dynamic: this.querySelector('[data-pickup-dynamic]'),
        modalError: this.querySelector('[data-pickup-error]'),
        live: this.querySelector('[data-pickup-live]'),
        stepTitle: this.querySelector('[data-pickup-step-title]'),
        stepDescription: this.querySelector('[data-pickup-step-description]')
      };

      this.abortController = new AbortController();
      var signal = this.abortController.signal;
      this.refs.openButton?.addEventListener('click', this.openModal, { signal: signal });
      this.refs.closeButton?.addEventListener('click', this.closeModal, { signal: signal });
      this.refs.cancelButton?.addEventListener('click', this.closeModal, { signal: signal });
      this.refs.backButton?.addEventListener('click', this.handleBack, { signal: signal });
      this.refs.primaryButton?.addEventListener('click', this.handlePrimary, { signal: signal });
      this.refs.modal?.addEventListener('keydown', this.handleKeydown, { signal: signal });
      this.refs.overlay?.addEventListener('click', this.handleOverlayClick, { signal: signal });
      this.refs.form?.addEventListener('submit', this.handleFormSubmit, { signal: signal });
      document.addEventListener('shopify:section:unload', this.handleSectionUnload, { signal: signal });
      document.addEventListener('shopify:section:load', this.handleSectionLoad, { signal: signal });
      document.addEventListener('cart:update', this.handleCartUpdate, { signal: signal });

      this.hydrateCustomer();
      this.updateTriggerState();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.requestController?.abort();
      this.restoreBodyScroll();
    }

    get labels() {
      return this.config.labels || {};
    }

    debug() {
      if (!this.config.debugMode) return;
      console.debug.apply(console, ['[in-store-pickup]'].concat(Array.from(arguments)));
    }

    updateTriggerState() {
      var count = Number(this.dataset.cartItemCount || 0);
      var disabled = !this.config.enabled || count < 1 || this.state.loading;
      if (this.refs.openButton) this.refs.openButton.disabled = disabled;
      if (count < 1) this.setTriggerError(this.labels.emptyCart);
      else if (!this.config.enabled) this.setTriggerError(this.labels.endpointMissing);
      else this.setTriggerError('');
    }

    setTriggerError(message) {
      if (this.refs.triggerError) this.refs.triggerError.textContent = message || '';
    }

    hydrateCustomer() {
      if (!this.refs.form) return;
      var stored = readStoredCustomer() || {};
      // On a shared device, don't prefill another person's data over a logged-in account.
      if (this.customer.email && stored.email && stored.email.toLowerCase() !== this.customer.email.toLowerCase()) {
        stored = {};
      }
      var values = {
        firstName: this.customer.firstName || stored.firstName || '',
        lastName: this.customer.lastName || stored.lastName || '',
        email: this.customer.email || stored.email || '',
        phone: this.customer.phone || stored.phone || '',
        contactPreference: stored.contactPreference || ''
      };
      Object.keys(values).forEach((key) => {
        var field = this.refs.form.querySelector('[data-pickup-field="' + key + '"]');
        if (!field || !values[key]) return;
        if (field.tagName === 'SELECT') {
          var hasOption = Array.from(field.options).some(function (option) { return option.value === values[key]; });
          if (hasOption) field.value = values[key];
        } else if (!field.value) {
          field.value = values[key];
        }
      });
    }

    saveCustomer(customer) {
      var stored = readStoredCustomer() || {};
      var fields = pickStoredFields(customer);
      var signature = JSON.stringify(fields);
      writeStoredCustomer(Object.assign({}, fields, {
        // Keep the sync marker only while the data is unchanged, so edits are re-sent to Shopify.
        syncedSignature: stored.syncedSignature === signature ? signature : null
      }));
      return signature;
    }

    // Fire-and-forget: creates/tags the customer in Shopify through the POS. It never blocks the
    // pickup flow and uses its own controller so it can't abort the availability request.
    syncShopifyCustomer(customer, signature) {
      if (!this.config.customerPath) return;
      var stored = readStoredCustomer() || {};
      if (stored.syncedSignature === signature) return;

      var url;
      try {
        url = this.buildEndpoint(this.config.customerPath);
      } catch (error) {
        this.debug(error);
        return;
      }

      var controller = new AbortController();
      var timeout = window.setTimeout(function () { controller.abort(); }, Number(this.config.timeoutMs) || 15000);
      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        signal: controller.signal,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer: pickStoredFields(customer),
          source: 'pickup_form',
          clientContext: {
            locale: this.config.locale,
            source: this.config.source
          }
        })
      })
        .then((response) => {
          if (!response.ok) throw new Error('Pickup customer sync failed: ' + response.status);
          var current = readStoredCustomer() || {};
          if (JSON.stringify(pickStoredFields(current)) === signature) {
            writeStoredCustomer(Object.assign(current, { syncedSignature: signature }));
          }
          this.trackEvent('pickup_customer_synced');
        })
        .catch((error) => this.debug(error))
        .finally(function () { window.clearTimeout(timeout); });
    }

    openModal = async () => {
      if (this.state.loading || !this.config.enabled) return;
      if (Number(this.dataset.cartItemCount || 0) < 1) {
        this.setTriggerError(this.labels.emptyCart);
        return;
      }
      try {
        this.validateEndpointConfig();
      } catch (error) {
        this.setTriggerError(this.labels.endpointMissing || this.labels.generalError);
        return;
      }

      this.previousActiveElement = document.activeElement;
      this.refs.overlay.hidden = false;
      document.body.style.overflow = 'hidden';
      this.setStep('customer');
      this.refs.modal?.focus();
      this.trackEvent('pickup_reservation_opened', { customer_logged_in: Boolean(this.customer.loggedIn) });

      if (!this.locationsLoaded) {
        await this.loadLocations();
      }
    };

    closeModal = () => {
      if (this.state.loading && (this.state.step === 'validating' || this.state.step === 'submitting')) return;
      this.requestController?.abort();
      this.refs.overlay.hidden = true;
      this.clearError();
      this.restoreBodyScroll();
      if (this.previousActiveElement && typeof this.previousActiveElement.focus === 'function') {
        this.previousActiveElement.focus();
      }
      this.trackEvent('pickup_modal_closed');
      if (this.state.step === 'success' && this.state.cartCleared) this.refreshSideCart();
    };

    // Swapping the drawer markup removes this element, so it only runs once the modal is closed.
    refreshSideCart() {
      var markup = this.state.clearedCartMarkup;
      if (markup && typeof window.fsRefreshCart === 'function') {
        Promise.resolve(window.fsRefreshCart(markup, 0)).then(function (updated) {
          if (!updated) window.location.reload();
        });
      } else {
        window.location.reload();
      }
    }

    restoreBodyScroll() {
      document.body.style.overflow = '';
    }

    handleOverlayClick = (event) => {
      if (event.target !== this.refs.overlay) return;
      this.closeModal();
    };

    handleFormSubmit = (event) => {
      event.preventDefault();
      this.handlePrimary();
    };

    handleSectionUnload = (event) => {
      if (event.target && event.target.contains(this)) this.disconnectedCallback();
    };

    handleSectionLoad = () => {
      this.updateTriggerState();
    };

    handleCartUpdate = (event) => {
      var itemCount = event.detail?.resource?.item_count || event.detail?.data?.itemCount;
      if (typeof itemCount === 'number') {
        this.dataset.cartItemCount = String(itemCount);
        this.updateTriggerState();
      }
      if (!this.refs.overlay?.hidden && this.state.step !== 'customer' && this.state.step !== 'success') {
        this.state.validationToken = null;
        this.state.validatedCartSignature = null;
        this.showError(this.labels.cartChanged);
      }
    };

    handleKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeModal();
        return;
      }
      if (event.key !== 'Tab') return;
      var focusable = Array.from(this.refs.modal.querySelectorAll(SELECTOR_FOCUSABLE))
        .filter(function (element) {
          return element.offsetParent !== null || element === document.activeElement;
        });
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    handleBack = () => {
      if (this.state.loading) return;
      if (this.state.step === 'confirmation') {
        this.setStep('options');
      } else if (this.state.step === 'options' || this.state.step === 'error') {
        this.setStep('customer');
      }
    };

    handlePrimary = async () => {
      if (this.state.loading) return;
      if (this.state.step === 'customer') {
        await this.submitCustomerStep();
      } else if (this.state.step === 'options') {
        this.goToConfirmation();
      } else if (this.state.step === 'confirmation') {
        await this.createReservation();
      } else if (this.state.step === 'success') {
        this.closeModal();
      } else if (this.state.step === 'error') {
        this.setStep('customer');
      }
    };

    setLoading(isLoading) {
      this.state.loading = isLoading;
      this.dataset.loading = isLoading ? 'true' : 'false';
      if (this.refs.primaryButton) this.refs.primaryButton.disabled = isLoading;
      if (this.refs.backButton) this.refs.backButton.disabled = isLoading;
      if (this.refs.closeButton) this.refs.closeButton.disabled = isLoading && this.state.step === 'submitting';
      this.updateTriggerState();
    }

    setStep(step) {
      this.state.step = step;
      this.clearError();
      if (this.refs.form) this.refs.form.hidden = step !== 'customer';
      if (this.refs.dynamic) this.refs.dynamic.hidden = step === 'customer';
      if (this.refs.backButton) this.refs.backButton.hidden = step === 'customer' || step === 'validating' || step === 'success';
      if (this.refs.cancelButton) this.refs.cancelButton.hidden = step === 'success';

      var title = this.labels.customerTitle;
      var description = this.labels.customerDescription;
      var primary = this.labels.continue;

      if (step === 'validating') {
        title = this.labels.validationTitle;
        description = this.labels.validationMessage;
        primary = this.labels.loading;
        this.renderLoading(description);
      } else if (step === 'options') {
        title = this.labels.optionsTitle || this.labels.availabilityTitle;
        description = this.getAvailabilityIntro();
        primary = this.labels.continue;
        this.renderOptions();
      } else if (step === 'confirmation') {
        title = this.labels.confirmationTitle;
        description = this.labels.confirmationDescription;
        primary = this.labels.createReservation;
        this.renderConfirmation();
      } else if (step === 'submitting') {
        title = this.labels.confirmationTitle;
        description = this.labels.loading;
        primary = this.labels.loading;
        this.renderLoading(this.labels.loading);
      } else if (step === 'success') {
        title = this.labels.successTitle;
        description = this.labels.successMessage;
        primary = this.labels.continueShopping || this.labels.close;
        this.renderSuccess();
      } else if (step === 'error') {
        title = this.labels.availabilityTitle;
        description = this.labels.generalError;
        primary = this.labels.retry;
      }

      if (this.refs.stepTitle) this.refs.stepTitle.textContent = safeText(title);
      if (this.refs.stepDescription) this.refs.stepDescription.textContent = safeText(description);
      if (this.refs.primaryButton) this.refs.primaryButton.textContent = safeText(primary);
      this.announce(description || title);
    }

    announce(message) {
      if (this.refs.live) this.refs.live.textContent = message || '';
    }

    renderLoading(message) {
      var wrapper = createElement('div', 'in-store-pickup__loading-state');
      appendText(wrapper, 'p', 'in-store-pickup__summary-text', message || this.labels.loading);
      this.refs.dynamic.replaceChildren(wrapper);
    }

    async loadLocations() {
      this.setLoading(true);
      try {
        var data = this.config.testMode && window.Shopify?.designMode ? this.getMockLocations() : await this.fetchJson(this.buildEndpoint(this.config.locationsPath), { method: 'GET' });
        var locations = Array.isArray(data.locations) ? data.locations : [];
        locations = locations.filter(function (location) {
          return location && location.id && location.name && location.pickupEnabled !== false;
        });
        this.state.locations = locations;
        this.locationsLoaded = true;
        this.populateLocations();
        this.trackEvent('pickup_locations_loaded');
      } catch (error) {
        this.debug(error);
        this.showError(this.labels.locationsError || this.labels.generalError);
      } finally {
        this.setLoading(false);
      }
    }

    populateLocations() {
      var select = this.refs.form?.querySelector('[data-pickup-field="location"]');
      if (!select) return;
      while (select.options.length > 1) select.remove(1);
      this.state.locations.forEach(function (location) {
        var option = document.createElement('option');
        option.value = String(location.id);
        option.textContent = location.name;
        select.appendChild(option);
      });
    }

    validateEndpointConfig() {
      this.buildEndpoint(this.config.locationsPath);
      this.buildEndpoint(this.config.validatePath);
      this.buildEndpoint(this.config.createPath);
    }

    buildEndpoint(path) {
      var base = String(this.config.endpointBase || '').trim();
      var endpointPath = String(path || '').trim();
      if (!base || !endpointPath) throw new Error('Missing pickup endpoint');
      if (/^(javascript|data|file):/i.test(base) || /^(javascript|data|file):/i.test(endpointPath)) {
        throw new Error('Unsupported pickup endpoint protocol');
      }
      if (/^https?:\/\//i.test(endpointPath)) return endpointPath;
      if (/^https?:\/\//i.test(base)) {
        var url = new URL(base);
        url.pathname = [url.pathname.replace(/\/+$/, ''), endpointPath.replace(/^\/+/, '')].filter(Boolean).join('/');
        return url.toString();
      }
      return '/' + [base, endpointPath].map(function (part) {
        return String(part).replace(/^\/+|\/+$/g, '');
      }).filter(Boolean).join('/');
    }

    async fetchJson(url, options) {
      this.requestController?.abort();
      this.requestController = new AbortController();
      var timeout = window.setTimeout(() => this.requestController?.abort(), Number(this.config.timeoutMs) || 15000);
      var requestOptions = Object.assign({
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: this.requestController.signal
      }, options || {});

      try {
        var response = await fetch(url, requestOptions);
        var contentType = response.headers.get('content-type') || '';
        var data = contentType.indexOf('application/json') > -1 ? await response.json() : null;
        if (!response.ok) {
          var error = new Error('Pickup request failed');
          error.status = response.status;
          error.code = data?.code || this.statusToCode(response.status);
          throw error;
        }
        if (!data || typeof data !== 'object') throw new Error('Invalid pickup JSON response');
        return data;
      } finally {
        window.clearTimeout(timeout);
      }
    }

    statusToCode(status) {
      if (status === 409) return 'CART_CHANGED';
      if (status === 422) return 'CUSTOMER_DATA_INVALID';
      if (status === 429) return 'RATE_LIMITED';
      return 'UNKNOWN_ERROR';
    }

    async submitCustomerStep() {
      var validation = this.validateCustomerForm();
      if (!validation.valid) {
        this.showError(this.labels.requiredFields);
        return;
      }
      this.state.customer = validation.customer;
      this.state.selectedLocationId = validation.selectedLocationId;
      var signature = this.saveCustomer(validation.customer);
      this.syncShopifyCustomer(validation.customer, signature);
      this.trackEvent('pickup_customer_data_submitted', { customer_logged_in: Boolean(this.customer.loggedIn) });
      this.trackEvent('pickup_location_selected', { location_id: this.state.selectedLocationId });
      await this.validateAvailability();
    }

    validateCustomerForm() {
      var form = this.refs.form;
      var errors = {};
      var firstName = form.querySelector('[data-pickup-field="firstName"]').value.trim();
      var lastName = form.querySelector('[data-pickup-field="lastName"]').value.trim();
      var email = form.querySelector('[data-pickup-field="email"]').value.trim();
      var phone = normalizePhone(form.querySelector('[data-pickup-field="phone"]').value);
      var selectedLocationId = form.querySelector('[data-pickup-field="location"]').value;
      var notes = form.querySelector('[data-pickup-field="notes"]')?.value.trim() || '';
      var contactPreference = form.querySelector('[data-pickup-field="contactPreference"]')?.value || '';
      var termsField = form.querySelector('[data-pickup-field="terms"]');
      var locationExists = this.state.locations.some(function (location) {
        return String(location.id) === String(selectedLocationId);
      });

      if (!firstName) errors.firstName = this.labels.requiredFields;
      if (!lastName) errors.lastName = this.labels.requiredFields;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = this.labels.customerInvalid || this.labels.requiredFields;
      if (!/^\+?[0-9]{6,18}$/.test(phone)) errors.phone = this.labels.customerInvalid || this.labels.requiredFields;
      if (!selectedLocationId || !locationExists) errors.location = this.labels.locationUnavailable || this.labels.requiredFields;
      if (this.config.requireTerms && termsField && !termsField.checked) errors.terms = this.labels.requiredFields;

      this.renderFieldErrors(errors);
      return {
        valid: Object.keys(errors).length === 0,
        selectedLocationId: selectedLocationId,
        customer: {
          firstName: firstName,
          lastName: lastName,
          email: email,
          phone: phone,
          notes: notes,
          contactPreference: contactPreference,
          acceptedTerms: this.config.requireTerms ? Boolean(termsField?.checked) : true
        }
      };
    }

    renderFieldErrors(errors) {
      this.refs.form.querySelectorAll('[data-pickup-error-for]').forEach(function (element) {
        var key = element.dataset.pickupErrorFor;
        element.textContent = errors[key] || '';
      });
      this.refs.form.querySelectorAll('[data-pickup-field]').forEach(function (field) {
        var key = field.dataset.pickupField;
        if (errors[key]) field.setAttribute('aria-invalid', 'true');
        else field.removeAttribute('aria-invalid');
      });
    }

    async validateAvailability() {
      this.setStep('validating');
      this.setLoading(true);
      this.trackEvent('pickup_inventory_validation_started');
      try {
        var cart = await this.loadCart();
        if (!cart.item_count) throw this.makeCodeError('CART_CHANGED');
        var payload = {
          cartToken: cart.token,
          selectedLocationId: this.state.selectedLocationId,
          customer: this.state.customer,
          items: this.mapCartItems(cart.items),
          pickupDiscountPercent: this.getPickupDiscountPercent(),
          currency: cart.currency || this.config.currency,
          locale: this.config.locale,
          source: this.config.source,
          clientContext: {
            locale: this.config.locale,
            currency: cart.currency || this.config.currency,
            source: this.config.source
          }
        };
        var data = this.config.testMode && window.Shopify?.designMode ? this.getMockValidation(cart) : await this.fetchJson(this.buildEndpoint(this.config.validatePath), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        if (!data || data.valid !== true || !data.validationToken) throw this.makeCodeError(data?.code || 'UNKNOWN_ERROR');
        this.state.cart = cart;
        this.state.validatedCartSignature = this.getCartSignature(cart);
        this.state.validation = data;
        this.state.validationToken = data.validationToken;
        this.state.validationExpiresAt = data.expiresAt || null;
        this.state.options = this.normalizeOptions(data);
        this.state.selectedOptionId = this.state.options[0]?.id || null;
        this.trackEvent('pickup_inventory_validated', {
          item_count: cart.item_count,
          cart_value: cart.total_price,
          currency: cart.currency || this.config.currency
        });
        this.setStep('options');
      } catch (error) {
        this.debug(error);
        this.showError(this.messageForError(error));
        this.setStep('error');
      } finally {
        this.setLoading(false);
      }
    }

    async loadCart() {
      var response = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Unable to load cart');
      var cart = await response.json();
      // Liquid supplies compare_at_price and all discount allocations for the current cart.
      var pricingResponse = await fetch(this.config.cartPricingPath, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'text/html' }
      });
      if (!pricingResponse.ok) throw new Error('Unable to load pickup pricing');
      var pricingDocument = new DOMParser().parseFromString(await pricingResponse.text(), 'text/html');
      var pricing = readJson(pricingDocument.querySelector('[data-pickup-cart-pricing]'), null);
      if (!Array.isArray(pricing) || pricing.length !== cart.items.length) throw this.makeCodeError('CART_CHANGED');
      cart.items.forEach((item) => {
        var line = pricing.find(function (entry) { return entry.lineKey === item.key; });
        if (!line || line.quantity !== item.quantity || line.finalPrice !== item.final_price ||
            line.originalPrice !== item.original_price || line.finalLinePrice !== item.final_line_price ||
            line.originalLinePrice !== item.original_line_price) throw this.makeCodeError('CART_CHANGED');
        item.compare_at_price = line.compareAtPrice;
        item.has_store_discount = line.hasStoreDiscount;
      });
      this.dataset.cartItemCount = String(cart.item_count || 0);
      return cart;
    }

    getPickupDiscountPercent() {
      var percent = Number(this.config.discountPercent ?? 0);
      if (!Number.isFinite(percent) || percent < 0 || percent > 100) throw this.makeCodeError('CART_CHANGED');
      return percent;
    }

    mapCartItems(items) {
      return (items || []).map(function (item) {
        return {
          lineKey: item.key,
          productId: item.product_id,
          variantId: item.variant_id,
          sku: item.sku || '',
          title: item.product_title || item.title,
          variantTitle: item.variant_title || '',
          quantity: item.quantity,
          finalPrice: item.final_price,
          originalPrice: item.original_price,
          originalLinePrice: item.original_line_price,
          finalLinePrice: item.final_line_price,
          compareAtPrice: item.compare_at_price,
          hasStoreDiscount: item.has_store_discount,
          properties: item.properties || {},
          sellingPlanAllocation: item.selling_plan_allocation || null,
          sellingPlanId: item.selling_plan_allocation?.selling_plan?.id || null,
          featuredImage: item.featured_image || null,
          url: item.url || ''
        };
      });
    }

    getCartSignature(cart) {
      var signatureItems = (cart.items || []).map(function (item) {
        return {
          key: item.key,
          variantId: item.variant_id,
          quantity: item.quantity,
          properties: stableJson(item.properties || {}),
          originalPrice: item.original_price,
          finalPrice: item.final_price,
          originalLinePrice: item.original_line_price,
          finalLinePrice: item.final_line_price,
          compareAtPrice: item.compare_at_price,
          hasStoreDiscount: item.has_store_discount,
          sellingPlanId: item.selling_plan_allocation?.selling_plan?.id || null
        };
      });
      return JSON.stringify({ items: signatureItems, pickupDiscountPercent: this.getPickupDiscountPercent() });
    }

    normalizeOptions(data) {
      var options = Array.isArray(data.options) ? data.options.filter((option) => {
        if (!option || option.eligible !== true || !option.id) return false;
        if (option.type === 'split_pickup' && !this.config.enableSplitPickup) return false;
        if (option.type === 'consolidated_transfer' && !this.config.enableConsolidation) return false;
        if (option.type === 'alternative_location' && !this.config.enableAlternativeLocations) return false;
        return true;
      }) : [];

      if (!options.length && data.valid) {
        options.push({
          id: 'single-location',
          type: 'single_location',
          eligible: true,
          title: this.labels.allAvailable,
          description: this.labels.validity24,
          destinationLocationId: this.state.selectedLocationId,
          pickupGroups: [{
            locationId: this.state.selectedLocationId,
            locationName: data.selectedLocation?.name || this.getLocationName(this.state.selectedLocationId),
            items: this.mapCartItems(this.state.cart?.items || [])
          }]
        });
      }
      return options;
    }

    getAvailabilityIntro() {
      var validation = this.state.validation || {};
      if (validation.selectedLocation?.availableForAllItems) return this.labels.allAvailable;
      if (this.state.options.some(function (option) { return option.type === 'consolidated_transfer'; })) return this.labels.transferText;
      if (this.state.options.some(function (option) { return option.type === 'alternative_location'; })) return this.labels.alternativeText;
      return this.labels.distributedInventory || this.labels.availabilityTitle;
    }

    renderOptions() {
      var wrapper = createElement('div', 'in-store-pickup__options');
      if (!this.state.options.length) {
        var empty = createElement('div', 'in-store-pickup__empty-state');
        appendText(empty, 'p', 'in-store-pickup__summary-text', this.labels.insufficientInventory);
        wrapper.appendChild(empty);
        this.refs.primaryButton.disabled = true;
        this.refs.dynamic.replaceChildren(wrapper);
        return;
      }

      this.refs.primaryButton.disabled = false;
      this.state.options.forEach((option, index) => {
        var label = createElement('label', 'in-store-pickup__option');
        var radio = document.createElement('input');
        radio.type = 'radio';
        radio.name = this.id + '-pickup-option';
        radio.value = option.id;
        radio.checked = this.state.selectedOptionId ? this.state.selectedOptionId === option.id : index === 0;
        if (radio.checked) this.state.selectedOptionId = option.id;
        radio.addEventListener('change', () => {
          this.state.selectedOptionId = option.id;
          this.trackEvent('pickup_option_selected', {
            pickup_mode: option.type,
            requires_transfer: option.type === 'consolidated_transfer',
            split_group_count: Array.isArray(option.pickupGroups) ? option.pickupGroups.length : undefined
          });
        });
        label.appendChild(radio);

        var body = createElement('div');
        appendText(body, 'h3', 'in-store-pickup__option-title', option.title || this.titleForOption(option.type));
        appendText(body, 'p', 'in-store-pickup__option-description', option.description || this.descriptionForOption(option.type));
        this.appendOptionMeta(body, option);
        this.appendPickupGroups(body, option.pickupGroups);
        label.appendChild(body);
        wrapper.appendChild(label);
        this.trackEvent('pickup_option_presented', { pickup_mode: option.type });
      });

      this.refs.dynamic.replaceChildren(wrapper);
    }

    appendOptionMeta(parent, option) {
      var pieces = [];
      if (option.estimatedReadyAt) pieces.push((this.labels.estimatedTime || '') + ': ' + this.formatDate(option.estimatedReadyAt));
      if (option.expiresAt || this.state.validationExpiresAt) pieces.push((this.labels.expiration || '') + ': ' + this.formatDate(option.expiresAt || this.state.validationExpiresAt));
      if (option.destinationLocationId) pieces.push((this.labels.location || '') + ': ' + this.getLocationName(option.destinationLocationId));
      appendText(parent, 'p', 'in-store-pickup__option-meta', pieces.join(' · '));
      if (Array.isArray(option.warnings)) {
        option.warnings.forEach(function (warning) {
          appendText(parent, 'p', 'in-store-pickup__option-meta', warning.message || warning);
        });
      }
    }

    appendPickupGroups(parent, groups) {
      if (!Array.isArray(groups) || !groups.length) return;
      var grid = createElement('div', 'in-store-pickup__option-groups');
      groups.forEach((group) => {
        var box = createElement('div', 'in-store-pickup__group');
        appendText(box, 'h4', 'in-store-pickup__group-title', group.locationName || this.getLocationName(group.locationId));
        appendText(box, 'p', 'in-store-pickup__summary-text', this.formatAddress(group.address) || group.hours || group.message || '');
        var items = Array.isArray(group.items) ? group.items : [];
        if (items.length) box.appendChild(this.renderProductList(items));
        grid.appendChild(box);
      });
      parent.appendChild(grid);
    }

    titleForOption(type) {
      if (type === 'split_pickup') return this.labels.splitTitle;
      if (type === 'consolidated_transfer') return this.labels.consolidationTitle;
      if (type === 'alternative_location') return this.labels.alternativeTitle;
      return this.labels.allAvailable;
    }

    descriptionForOption(type) {
      if (type === 'split_pickup') return this.labels.splitDescription;
      if (type === 'consolidated_transfer') return this.labels.consolidationDescription;
      if (type === 'alternative_location') return this.labels.alternativeDescription;
      return this.labels.validity24;
    }

    goToConfirmation() {
      if (!this.state.selectedOptionId) {
        this.showError(this.labels.requiredFields);
        return;
      }
      this.setStep('confirmation');
      this.trackEvent('pickup_confirmation_viewed', { pickup_mode: this.selectedOption()?.type });
    }

    renderConfirmation() {
      var option = this.selectedOption();
      var wrapper = createElement('div');
      var summary = createElement('div', 'in-store-pickup__summary-grid');
      summary.appendChild(this.summaryBox(this.labels.customerSummaryTitle, [
        this.state.customer.firstName + ' ' + this.state.customer.lastName,
        this.state.customer.phone,
        this.state.customer.email
      ]));
      summary.appendChild(this.summaryBox(this.labels.modeTitle, [
        option.title || this.titleForOption(option.type),
        option.description || this.descriptionForOption(option.type)
      ]));
      summary.appendChild(this.summaryBox(this.labels.locationTitle, this.locationLinesForOption(option)));
      summary.appendChild(this.summaryBox(this.labels.conditionsTitle, [
        this.labels.waitConfirmation,
        option.type === 'consolidated_transfer' ? this.labels.validity48 : this.labels.validity24
      ]));
      wrapper.appendChild(summary);

      appendText(wrapper, 'h3', 'in-store-pickup__summary-title', this.labels.productsTitle);
      wrapper.appendChild(this.renderProductList(this.mapCartItems(this.state.cart?.items || [])));

      var checkboxLabel = createElement('label', 'in-store-pickup__checkbox');
      var checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.dataset.pickupConfirmInventory = 'true';
      checkboxLabel.appendChild(checkbox);
      checkboxLabel.appendChild(createElement('span', '', this.labels.confirmInventoryCheckbox || this.labels.inventoryConfirmText));
      wrapper.appendChild(checkboxLabel);
      appendText(wrapper, 'p', 'in-store-pickup__conditions', this.labels.inventoryConfirmText);
      this.refs.dynamic.replaceChildren(wrapper);
    }

    summaryBox(title, lines) {
      var box = createElement('div', 'in-store-pickup__summary-box');
      appendText(box, 'h3', 'in-store-pickup__summary-title', title);
      (lines || []).filter(Boolean).forEach(function (line) {
        appendText(box, 'p', 'in-store-pickup__summary-text', line);
      });
      return box;
    }

    locationLinesForOption(option) {
      var lines = [];
      if (option.destinationLocationId) lines.push(this.getLocationName(option.destinationLocationId));
      if (Array.isArray(option.pickupGroups)) {
        option.pickupGroups.forEach((group) => lines.push(group.locationName || this.getLocationName(group.locationId)));
      }
      if (!lines.length && this.state.selectedLocationId) lines.push(this.getLocationName(this.state.selectedLocationId));
      if (option.estimatedReadyAt) lines.push((this.labels.estimatedDate || '') + ': ' + this.formatDate(option.estimatedReadyAt));
      return Array.from(new Set(lines.filter(Boolean)));
    }

    renderProductList(items) {
      var list = createElement('ul', 'in-store-pickup__list');
      (items || []).forEach((item) => {
        var product = createElement('li', 'in-store-pickup__product');
        var imageSrc = typeof item.featuredImage === 'string' ? item.featuredImage : item.featuredImage?.url;
        if (imageSrc) {
          var image = document.createElement('img');
          image.src = imageSrc;
          image.alt = '';
          image.loading = 'lazy';
          product.appendChild(image);
        } else {
          product.appendChild(this.renderProductPlaceholder(item.productId));
        }
        var copy = createElement('div');
        appendText(copy, 'p', 'in-store-pickup__product-title', item.title);
        appendText(copy, 'p', 'in-store-pickup__product-meta', [item.variantTitle, 'Cantidad: ' + item.quantity].filter(Boolean).join(' · '));
        product.appendChild(copy);
        list.appendChild(product);
      });
      return list;
    }

    renderProductPlaceholder(productId) {
      var wrapper = createElement('span', 'in-store-pickup__product-placeholder');
      var template = productId ? this.querySelector('template[data-pickup-placeholder="' + String(productId) + '"]') : null;
      if (template) wrapper.appendChild(template.content.cloneNode(true));
      return wrapper;
    }

    async createReservation() {
      var confirmField = this.refs.dynamic.querySelector('[data-pickup-confirm-inventory]');
      if (confirmField && !confirmField.checked) {
        this.showError(this.labels.confirmInventoryCheckbox || this.labels.requiredFields);
        return;
      }

      this.setStep('submitting');
      this.setLoading(true);
      this.trackEvent('pickup_reservation_submitted', { pickup_mode: this.selectedOption()?.type });

      try {
        var currentCart = await this.loadCart();
        if (this.getCartSignature(currentCart) !== this.state.validatedCartSignature) {
          this.state.validationToken = null;
          throw this.makeCodeError('CART_CHANGED');
        }
        if (!this.pendingReservationPayload) {
          this.idempotencyKey = this.idempotencyKey || generateIdempotencyKey();
          this.pendingReservationPayload = {
            validationToken: this.state.validationToken,
            cartToken: currentCart.token,
            customer: this.state.customer,
            decision: {
              optionId: this.state.selectedOptionId,
              type: this.selectedOption()?.type,
              selectedLocationId: this.state.selectedLocationId,
              acceptedTerms: this.state.customer.acceptedTerms
            },
            items: this.mapCartItems(currentCart.items),
            pickupDiscountPercent: this.getPickupDiscountPercent(),
            clientContext: {
              locale: this.config.locale,
              currency: currentCart.currency || this.config.currency,
              source: this.config.source
            }
          };
        }
        var data = this.config.testMode && window.Shopify?.designMode ? this.getMockReservation() : await this.fetchJson(this.buildEndpoint(this.config.createPath), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'Idempotency-Key': this.idempotencyKey
          },
          body: JSON.stringify(this.pendingReservationPayload)
        });
        if (!data || data.success !== true) throw this.makeCodeError(data?.code || 'UNKNOWN_ERROR');
        this.state.reservation = data;
        this.trackEvent('pickup_reservation_created', { pickup_mode: data.pickupMode || this.selectedOption()?.type });
        await this.afterReservationSuccess();
        this.setStep('success');
      } catch (error) {
        this.debug(error);
        this.pendingReservationPayload = this.pendingReservationPayload || null;
        this.trackEvent('pickup_reservation_failed', { error_code: error.code || 'UNKNOWN_ERROR' });
        if (error.code === 'CART_CHANGED') {
          this.showError(this.labels.cartChanged);
          this.setStep('customer');
        } else {
          this.showError(this.messageForError(error));
          this.setStep('confirmation');
        }
      } finally {
        this.setLoading(false);
      }
    }

    async afterReservationSuccess() {
      if (this.config.postReservationCartBehavior === 'keep') return;
      try {
        var response = await fetch('/cart/clear.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ sections: 'fs-side-cart' })
        });
        if (!response.ok) throw new Error('Unable to clear cart');
        var data = await response.json().catch(function () { return {}; });
        this.state.cartCleared = true;
        this.state.clearedCartMarkup = data.sections?.['fs-side-cart'] || null;
        this.dataset.cartItemCount = '0';
        this.updateHeaderCartCount(0);
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: { item_count: 0 }, data: { source: 'in-store-pickup', itemCount: 0 } }
        }));
      } catch (error) {
        this.state.nonBlockingWarning = this.labels.clearCartWarning;
      }
    }

    updateHeaderCartCount(itemCount) {
      var headerCartCount = document.getElementById('fsCartCount');
      if (!headerCartCount) return;
      if (itemCount > 0) headerCartCount.textContent = String(itemCount);
      else headerCartCount.remove();
    }

    renderSuccess() {
      var reservation = this.state.reservation || {};
      var wrapper = createElement('div');
      wrapper.appendChild(createElement('div', 'in-store-pickup__success-mark', '✓'));
      appendText(wrapper, 'p', 'in-store-pickup__summary-text', reservation.message || this.labels.successMessage);
      if (this.config.showEmail) appendText(wrapper, 'p', 'in-store-pickup__summary-text', this.labels.emailMessage);
      if (this.config.showWhatsapp) appendText(wrapper, 'p', 'in-store-pickup__summary-text', this.labels.whatsappMessage);

      var grid = createElement('div', 'in-store-pickup__success-grid');
      grid.appendChild(this.summaryBox(this.labels.reservationNumber, [reservation.reservationId]));
      grid.appendChild(this.summaryBox(this.labels.estimatedDate, [this.formatDate(reservation.estimatedReadyAt)]));
      grid.appendChild(this.summaryBox(this.labels.expirationDate, [this.formatDate(reservation.pickupExpiresAt)]));
      grid.appendChild(this.summaryBox(this.labels.location, (reservation.locations || []).map(function (location) { return location.name; })));
      wrapper.appendChild(grid);
      if (this.state.nonBlockingWarning) appendText(wrapper, 'p', 'in-store-pickup__conditions', this.state.nonBlockingWarning);
      this.refs.dynamic.replaceChildren(wrapper);
    }

    selectedOption() {
      return this.state.options.find((option) => option.id === this.state.selectedOptionId) || this.state.options[0] || {};
    }

    getLocationName(id) {
      var location = this.state.locations.find(function (item) {
        return String(item.id) === String(id);
      });
      return location?.name || String(id || '');
    }

    formatAddress(address) {
      if (!address || typeof address !== 'object') return '';
      return [address.address1, address.address2, address.city, address.province, address.zip, address.country].filter(Boolean).join(', ');
    }

    formatDate(value) {
      if (!value) return '';
      var date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      try {
        return new Intl.DateTimeFormat(this.config.locale || undefined, {
          dateStyle: 'medium',
          timeStyle: 'short'
        }).format(date);
      } catch (error) {
        return date.toLocaleString();
      }
    }

    showError(message) {
      if (!this.refs.modalError) return;
      this.refs.modalError.textContent = message || this.labels.generalError || '';
      this.refs.modalError.hidden = false;
      this.announce(this.refs.modalError.textContent);
    }

    clearError() {
      if (!this.refs.modalError) return;
      this.refs.modalError.textContent = '';
      this.refs.modalError.hidden = true;
    }

    messageForError(error) {
      var key = ERROR_CODES[error?.code] || ERROR_CODES.UNKNOWN_ERROR;
      return this.labels[key] || this.labels.generalError || 'No pudimos completar la solicitud.';
    }

    makeCodeError(code) {
      var error = new Error(code);
      error.code = code;
      return error;
    }

    trackEvent(eventName, payload) {
      var data = Object.assign({ event: eventName }, payload || {});
      Object.keys(data).forEach(function (key) {
        if (data[key] === undefined || data[key] === null) delete data[key];
      });
      if (Array.isArray(window.dataLayer)) window.dataLayer.push(data);
      this.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: data }));
    }

    getMockLocations() {
      return {
        locations: [
          {
            id: 'fitstreet-centro',
            name: 'FitStreet Centro',
            address: { address1: 'Sucursal de prueba', city: 'Monterrey', province: 'Nuevo Leon' },
            phone: '+52 81 0000 0000',
            hours: 'Lunes a sabado, 10:00 a 19:00',
            pickupEnabled: true,
            message: 'Modo de prueba del Theme Editor'
          }
        ]
      };
    }

    getMockValidation(cart) {
      return {
        valid: true,
        validationToken: 'theme-editor-validation-token',
        expiresAt: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
        selectedLocation: {
          id: this.state.selectedLocationId,
          name: this.getLocationName(this.state.selectedLocationId),
          availableForAllItems: true
        },
        options: [{
          id: 'single-location',
          type: 'single_location',
          eligible: true,
          title: this.labels.allAvailable,
          description: this.labels.validity24,
          destinationLocationId: this.state.selectedLocationId,
          pickupGroups: [{
            locationId: this.state.selectedLocationId,
            locationName: this.getLocationName(this.state.selectedLocationId),
            items: this.mapCartItems(cart.items)
          }]
        }],
        warnings: []
      };
    }

    getMockReservation() {
      return {
        success: true,
        reservationId: 'RSV-THEME-EDITOR',
        status: 'pending_preparation',
        pickupMode: this.selectedOption()?.type || 'single_location',
        estimatedReadyAt: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
        pickupExpiresAt: new Date(Date.now() + 26 * 60 * 60 * 1000).toISOString(),
        locations: [{ id: this.state.selectedLocationId, name: this.getLocationName(this.state.selectedLocationId) }],
        message: this.labels.successMessage
      };
    }
  }

  if (!customElements.get('in-store-pickup')) {
    customElements.define('in-store-pickup', InStorePickup);
  }
})();
